import { useEffect, useState } from "react";
import { KDF_ITERATIONEN, leiteSchluesselAb } from "../../shared/kdf.js";
import { api } from "../api.js";

interface Einladung {
  name: string;
  zweck: "einladung" | "reset";
  salt: string;
  iterationen: number;
}

/**
 * Öffentliche Seite zum Einlösen eines Einladungs- oder Reset-Links.
 *
 * Läuft ohne Anmeldung. Der Token steht im Fragment der Adresse und wird
 * von App.tsx vor dem ersten Profilabruf herausgelöst und aus dem Verlauf
 * entfernt.
 *
 * Den Salt zieht hier der SERVER (er liegt in der Einladung), nicht der
 * Browser — anders als beim Passwortwechsel. Damit kann ein manipulierter
 * Client keine schwache Ableitung erzwingen.
 */
export function EinladungEinloesen({ token }: { token: string }) {
  const [inv, setInv] = useState<Einladung | null>(null);
  const [pruefFehler, setPruefFehler] = useState<string | null>(null);
  const [passwort, setPasswort] = useState("");
  const [wdh, setWdh] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  useEffect(() => {
    api
      .post<Einladung>("/einladung/pruefen", { token })
      .then(setInv)
      .catch((e) => setPruefFehler(e instanceof Error ? e.message : "Der Link ist ungültig."));
  }, [token]);

  async function absenden(e: React.FormEvent) {
    e.preventDefault();
    setFehler(null);
    // Der Server sieht das Klartextpasswort nie und kann die Mindestlänge
    // nicht nachprüfen — sie muss hier geprüft werden.
    if (passwort.length < 12) return setFehler("Das Passwort muss mindestens 12 Zeichen haben.");
    if (passwort !== wdh) return setFehler("Die beiden Passwörter stimmen nicht überein.");
    if (!inv) return;

    setLaeuft(true);
    try {
      const abgeleitet = await leiteSchluesselAb(passwort, inv.salt, inv.iterationen);
      await api.post("/einladung/einloesen", { token, abgeleitet });
      // Angemeldet — die Anwendung neu laden, damit App.tsx das Profil holt.
      location.replace("/");
    } catch (err) {
      setFehler(err instanceof Error ? err.message : "Einlösen fehlgeschlagen.");
      setLaeuft(false);
    }
  }

  return (
    <div className="anmelde-seite">
      <div className="anmelde-karte" style={{ maxWidth: 440 }}>
        <img src="/logo-engelke.png" alt="Gebr. Engelke — Große Mühle seit 1714" />

        {pruefFehler && (
          <>
            <div className="meldung fehler" role="alert">
              {pruefFehler}
            </div>
            <p className="hinweistext" style={{ textAlign: "center" }}>
              Bitte einen neuen Link beim Administrator anfordern.
            </p>
          </>
        )}

        {!inv && !pruefFehler && <p className="leer">Link wird geprüft …</p>}

        {inv && (
          <form onSubmit={absenden}>
            <div className="meldung hinweis" style={{ marginBottom: "1.4rem" }}>
              {inv.zweck === "reset" ? (
                <>
                  Hallo {inv.name}, hier können Sie ein neues Passwort vergeben. Alle bisherigen
                  Sitzungen wurden bereits beendet.
                </>
              ) : (
                <>
                  Willkommen, {inv.name}. Wählen Sie bitte Ihr Passwort — danach ist Ihr Zugang
                  aktiv.
                </>
              )}
            </div>

            {fehler && (
              <div className="meldung fehler" role="alert">
                {fehler}
              </div>
            )}

            <div className="feld">
              <label htmlFor="e-pw">Passwort</label>
              <input
                id="e-pw"
                type="password"
                autoComplete="new-password"
                required
                minLength={12}
                maxLength={128}
                autoFocus
                value={passwort}
                onChange={(e) => setPasswort(e.target.value)}
              />
              <p className="hinweistext">
                Mindestens 12 Zeichen. Länge zählt mehr als Sonderzeichen — vier zufällige Wörter
                sind eine gute Wahl.
              </p>
            </div>

            <div className="feld">
              <label htmlFor="e-wdh">Passwort wiederholen</label>
              <input
                id="e-wdh"
                type="password"
                autoComplete="new-password"
                required
                maxLength={128}
                value={wdh}
                onChange={(e) => setWdh(e.target.value)}
              />
            </div>

            <button className="knopf" type="submit" disabled={laeuft}>
              {laeuft ? "Wird gesetzt …" : "Passwort setzen"}
            </button>
            {laeuft && (
              <p className="hinweistext" style={{ textAlign: "center" }}>
                Das Passwort wird im Browser {KDF_ITERATIONEN.toLocaleString("de-DE")} Mal
                verschlüsselt. Auf älteren Geräten dauert das einen Moment.
              </p>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
